import { CtaCreateDTO, CtaInterface, CtaUpdateDTO, WidgetCtaAttachmentInterface } from "@swivl-mono/types";

import { APIService } from "../Service/API.Service";

/**
 * Studio-side client for the CTAs Library + the widget-attachment surface.
 * Org-scoped — CTAs are global per org, attached to specific widgets via
 * `WidgetCtaAttachment`.
 *
 * Per docs/snowy/widget/2026-05-18-widget-builder-plan.md §B2.
 */
export class CtasAPI {
  static async list(orgId: number) {
    return APIService.Server.get<CtaInterface[]>(`/ctas/org/${orgId}`);
  }

  static async get(orgId: number, ctaId: number) {
    return APIService.Server.get<CtaInterface>(`/ctas/org/${orgId}/cta/${ctaId}`);
  }

  static async create(orgId: number, dto: CtaCreateDTO) {
    return APIService.Server.post<CtaInterface>(`/ctas/org/${orgId}`, dto);
  }

  static async update(orgId: number, ctaId: number, dto: CtaUpdateDTO) {
    return APIService.Server.put<CtaInterface>(`/ctas/org/${orgId}/cta/${ctaId}`, dto);
  }

  static async publish(orgId: number, ctaId: number) {
    return APIService.Server.post<CtaInterface>(`/ctas/org/${orgId}/cta/${ctaId}/publish`);
  }

  static async unpublish(orgId: number, ctaId: number) {
    return APIService.Server.post<CtaInterface>(`/ctas/org/${orgId}/cta/${ctaId}/unpublish`);
  }

  static async archive(orgId: number, ctaId: number) {
    return APIService.Server.post<CtaInterface>(`/ctas/org/${orgId}/cta/${ctaId}/archive`);
  }

  static async unarchive(orgId: number, ctaId: number) {
    return APIService.Server.post<CtaInterface>(`/ctas/org/${orgId}/cta/${ctaId}/unarchive`);
  }

  static async duplicate(orgId: number, ctaId: number) {
    return APIService.Server.post<CtaInterface>(`/ctas/org/${orgId}/cta/${ctaId}/duplicate`);
  }

  static async remove(orgId: number, ctaId: number) {
    return APIService.Server.delete(`/ctas/org/${orgId}/cta/${ctaId}`);
  }

  // ───────── Widget attachments ──────────────────────────────────────────

  static async listAttachments(orgId: number, widgetId: number) {
    return APIService.Server.get<WidgetCtaAttachmentInterface[]>(`/widget-cta-attachments/org/${orgId}/widget/${widgetId}`);
  }

  static async listAllAttachments(orgId: number) {
    return APIService.Server.get<WidgetCtaAttachmentInterface[]>(`/widget-cta-attachments/org/${orgId}`);
  }

  static async attach(orgId: number, widgetId: number, ctaId: number, enabled = true) {
    return APIService.Server.post<WidgetCtaAttachmentInterface>(`/widget-cta-attachments/org/${orgId}/widget/${widgetId}`, { ctaId, enabled });
  }

  static async setAttachmentEnabled(orgId: number, attachmentId: number, enabled: boolean) {
    return APIService.Server.put<WidgetCtaAttachmentInterface>(`/widget-cta-attachments/org/${orgId}/attachment/${attachmentId}/enabled`, {
      enabled
    });
  }

  static async detach(orgId: number, attachmentId: number) {
    return APIService.Server.delete(`/widget-cta-attachments/org/${orgId}/attachment/${attachmentId}`);
  }
}
